/**
 * Browser-side safety copy of an unsaved document form (GRN today).
 *
 * Long manual entry lives only in component memory until the user saves, so a
 * crash, a closed tab or a forced navigation loses all of it. The form writes
 * its state here every few seconds and offers it back when reopened.
 *
 * Keyed per user + store + document, so a shared terminal never offers one
 * user's draft to another. Entries older than `maxAgeMs` are dropped on read.
 * Every storage call is guarded: a full or blocked localStorage only means no
 * safety copy, never a broken form.
 */
export interface StoredDraft<T> {
  savedAt: number;
  data: T;
}

const PREFIX = 'form-draft:';
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export class FormDraftStore<T> {
  private readonly key: string;
  private lastJson = '';

  constructor(
    parts: Array<string | null | undefined>,
    private readonly maxAgeMs = DEFAULT_MAX_AGE_MS,
  ) {
    this.key = PREFIX + parts.map((p) => p || '-').join(':');
  }

  load(): StoredDraft<T> | null {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;
      const draft = JSON.parse(raw) as StoredDraft<T>;
      if (!draft?.savedAt || Date.now() - draft.savedAt > this.maxAgeMs) {
        this.clear();
        return null;
      }
      return draft;
    } catch {
      return null;
    }
  }

  /** Writes only when the data changed since the last save. */
  save(data: T): void {
    let json: string;
    try {
      json = JSON.stringify(data);
    } catch {
      return; // not serialisable (cycle): skip rather than break the form
    }
    if (json === this.lastJson) return;
    try {
      localStorage.setItem(this.key, `{"savedAt":${Date.now()},"data":${json}}`);
      this.lastJson = json;
    } catch {
      /* quota exceeded / storage blocked */
    }
  }

  clear(): void {
    this.lastJson = '';
    try {
      localStorage.removeItem(this.key);
    } catch {
      /* storage blocked */
    }
  }
}
